import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query';
import {
  EndpointBuilder,
  MutationLifecycleApi,
  QueryLifecycleApi,
} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { Config } from '../helpers/config';
import { RootState } from '../redux/core';
import {
  addLoaderFullPage,
  removeLoaderFullPage,
} from '../redux/general/general.actions';
import { v4 } from 'uuid';
import { SignInResponse } from '../models/auth.models';
import {
  removeFromLocalStorage,
  setOnLocalStorage,
  StorageKey,
} from '../helpers/localstorage';
import { SerializedError } from '@reduxjs/toolkit';
import { ApiError, ErrorModel } from '../models/error.models';
import {
  BaseQueryApi,
  QueryReturnValue,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { checkIfErrorIsAvoidable, getErrorDescription } from './apiErrorHelper';

export enum InvalidationTag {
  Order = 'Order',
  Addresses = 'Address',
  User = 'User',
  Cart = 'Cart',
}

export type BuilderType = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, {}>,
  InvalidationTag,
  'mlkApi'
>;

export const mlkOnQueryStarted: (
  arg: any,
  api:
    | QueryLifecycleApi<string, BaseQueryFn, any, string>
    | MutationLifecycleApi<any, BaseQueryFn, any, any>
) => void = async (_: any, { dispatch, queryFulfilled }) => {
  const uuid = v4();
  dispatch(addLoaderFullPage(uuid));
  try {
    await queryFulfilled;
  } catch (e) {
    console.error(e);
    throw e;
  } finally {
    dispatch(removeLoaderFullPage(uuid));
  }
};

export const prepareHeaders: (
  headers: Headers,
  api: {
    getState: () => unknown;
  }
) => MaybePromise<Headers> = (headers: Headers, { getState }) => {
  const authorizationHeader =
    (getState() as RootState).user.authToken ?? 'guest';

  headers.set('authorization', authorizationHeader);

  return headers;
};

export const mlkTrackingFetchBaseQuery = fetchBaseQuery({
  baseUrl: Config.baseApiUrl,
  prepareHeaders,
  mode: 'cors',
});

export const mlkWithRefreshAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await mlkTrackingFetchBaseQuery(args, api, extraOptions);

  if (
    result.error &&
    result.error.status === 401 &&
    !result.meta?.request.url.includes('/auth/login')
  ) {
    result = (await handleAuthRefresh(
      api,
      extraOptions,
      result,
      args
    )) as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
  } else if (result.error) {
    const error = result.error.data as ErrorModel | undefined;

    const isErrorAvoidable = checkIfErrorIsAvoidable(result, error);

    if (!isErrorAvoidable) {
      const errorDescription = getErrorDescription(
        error || { error: ApiError.ServerError }
      );
      api.dispatch({
        type: 'general/setErrorDescription',
        payload: errorDescription,
      });
    }
  }

  return result;
};

export const cmsFetchBaseQuery = fetchBaseQuery({
  baseUrl: Config.baseCmsUrl,
  mode: 'cors',
});

export const parseRes = <T>(
  res: { data: T } | { error: FetchBaseQueryError | SerializedError }
) => ({
  successRes: res as { data: T },
  errorRes: res as { error: FetchBaseQueryError },
});

let currentRefreshRequest: null | Promise<any> = null;

async function handleAuthRefresh(
  api: BaseQueryApi,
  extraOptions: {},
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  args: string | FetchArgs
) {
  let refreshResultData;
  if (!currentRefreshRequest) {
    const request = async () => {
      const refreshToken = (api.getState() as RootState).user.refreshToken;
      const refreshResult = await mlkTrackingFetchBaseQuery(
        {
          url: '/auth/refreshTokens',
          method: 'POST',
          body: { refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const refreshResultData = refreshResult.data as SignInResponse;
        updateTokens(api, refreshResultData);
      } else {
        removeTokens(api);
      }
      return refreshResult.data;
    };
    currentRefreshRequest = request();
  }
  refreshResultData = await currentRefreshRequest;
  if (refreshResultData) {
    return await mlkTrackingFetchBaseQuery(args, api, extraOptions);
  }
}

function removeTokens(api: BaseQueryApi) {
  api.dispatch({ type: 'user/setAccessToken', payload: null });
  api.dispatch({ type: 'user/setRefreshToken', payload: null });
  api.dispatch({ type: 'user/setAuthUser', payload: null });
  removeFromLocalStorage(StorageKey.AccessToken);
  removeFromLocalStorage(StorageKey.RefreshToken);
}

function updateTokens(api: BaseQueryApi, refreshResultData: SignInResponse) {
  // Using normal object as actions instead of action creators from the
  // user slice to avoid circular dependencies that make the app break
  api.dispatch({
    type: 'user/setAccessToken',
    payload: refreshResultData.tokens.accessToken,
  });
  api.dispatch({
    type: 'user/setRefreshToken',
    payload: refreshResultData.tokens.refreshToken,
  });
  api.dispatch({
    type: 'user/setAuthUser',
    payload: refreshResultData.user,
  });
  setOnLocalStorage(
    refreshResultData.tokens.accessToken,
    StorageKey.AccessToken
  );
  setOnLocalStorage(
    refreshResultData.tokens.refreshToken,
    StorageKey.RefreshToken
  );
}
